/**
 * Ra'i (راعي) — Portal Login Page
 *
 * Desert Dusk Pastoral aesthetic matching the domain frontend login.
 * Shepherd's staff SVG, bilingual branding, green sign-in button.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  PersonOutlineRounded,
  LockOutlined,
} from '@mui/icons-material';
import { useAuth } from '../auth/AuthProvider';

// ── Animated background dots (circuit pattern — tradition meets tech) ──
function CircuitDots() {
  const dots = Array.from({ length: 18 }, (_, i) => ({
    id: i,
    x: 10 + Math.random() * 80,
    y: 10 + Math.random() * 80,
    size: 2 + Math.random() * 4,
    delay: Math.random() * 4,
    duration: 3 + Math.random() * 4,
  }));

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    >
      {dots.map((d) => (
        <Box
          key={d.id}
          sx={{
            position: 'absolute',
            left: `${d.x}%`,
            top: `${d.y}%`,
            width: d.size,
            height: d.size,
            borderRadius: '50%',
            backgroundColor: '#F59E0B',
            opacity: 0.15,
            animation: `pulse-dot ${d.duration}s ease-in-out ${d.delay}s infinite`,
            '@keyframes pulse-dot': {
              '0%, 100%': { opacity: 0.08, transform: 'scale(1)' },
              '50%': { opacity: 0.3, transform: 'scale(1.8)' },
            },
          }}
        />
      ))}
    </Box>
  );
}

// ── Shepherd's Staff SVG Logo ──
function RaiLogo({ size = 64 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Staff */}
      <path
        d="M32 56V20C32 14 36 10 40 10C44 10 46 13 44 16C42 19 38 20 36 20"
        stroke="#1B5E20"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Circuit dots along the staff */}
      <circle cx="32" cy="28" r="2.5" fill="#F59E0B" />
      <circle cx="32" cy="38" r="2" fill="#F59E0B" opacity="0.7" />
      <circle cx="32" cy="47" r="1.5" fill="#F59E0B" opacity="0.5" />
      <circle cx="40" cy="13" r="2" fill="#F59E0B" />
      {/* Circuit lines */}
      <line x1="34.5" y1="28" x2="38" y2="28" stroke="#F59E0B" strokeWidth="0.8" opacity="0.4" />
      <line x1="34" y1="38" x2="36" y2="38" stroke="#F59E0B" strokeWidth="0.6" opacity="0.3" />
    </svg>
  );
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading, error, login } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as any)?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!username.trim()) { setFormError('Username is required'); return; }
    if (!password) { setFormError('Password is required'); return; }
    try {
      await login({ username: username.trim(), password });
    } catch {
      // Error captured in auth state
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: '"IBM Plex Sans", "IBM Plex Sans Arabic", sans-serif',
        // Desert dusk gradient — Jordanian hillside at golden hour
        background: `
          radial-gradient(ellipse at 20% 80%, rgba(27, 94, 32, 0.15) 0%, transparent 50%),
          radial-gradient(ellipse at 80% 20%, rgba(245, 158, 11, 0.12) 0%, transparent 50%),
          linear-gradient(
            175deg,
            #0D3B12 0%,
            #1B5E20 25%,
            #2E7D32 40%,
            #4A7C3F 55%,
            #8B7355 70%,
            #C4956A 82%,
            #E8B87A 90%,
            #F5D5A0 100%
          )
        `,
      }}
    >
      {/* Circuit pattern overlay */}
      <CircuitDots />

      {/* Login card */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 400,
          mx: 2,
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(20px)',
          transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* Card with glass morphism */}
        <Box
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.92)',
            backdropFilter: 'blur(24px)',
            borderRadius: '16px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
            p: { xs: 3.5, sm: 4.5 },
            textAlign: 'center',
          }}
        >
          {/* Logo */}
          <Box sx={{ mb: 1, display: 'flex', justifyContent: 'center' }}>
            <RaiLogo size={56} />
          </Box>

          {/* Brand name — Arabic prominent */}
          <Typography
            sx={{
              fontSize: '2.2rem',
              fontWeight: 700,
              color: '#1B5E20',
              fontFamily: '"IBM Plex Sans Arabic", "Amiri", serif',
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              mb: 0.3,
            }}
          >
            راعي
          </Typography>
          <Typography
            sx={{
              fontSize: '0.85rem',
              fontWeight: 500,
              color: '#6B7280',
              fontFamily: '"IBM Plex Sans", sans-serif',
              textTransform: 'uppercase',
              letterSpacing: '0.15em',
              mb: 1,
            }}
          >
            Ra'i
          </Typography>

          {/* Tagline */}
          <Typography
            sx={{
              fontSize: '0.8rem',
              color: '#9CA3AF',
              fontFamily: 'inherit',
              mb: 3.5,
              direction: 'ltr',
              textAlign: 'center',
            }}
          >
            إدارة ذكية للقطيع &nbsp;|&nbsp; Smart Flock Management
          </Typography>

          {/* Decorative line */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Box sx={{ flex: 1, height: '1px', background: 'linear-gradient(to right, transparent, #D1D5DB)' }} />
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#F59E0B', opacity: 0.5 }} />
            <Box sx={{ flex: 1, height: '1px', background: 'linear-gradient(to left, transparent, #D1D5DB)' }} />
          </Box>

          {/* Error display */}
          {(error || formError) && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: '10px',
                fontFamily: 'inherit',
                fontSize: '0.85rem',
                '& .MuiAlert-icon': { fontSize: 20 },
              }}
            >
              {formError || error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              autoComplete="username"
              autoFocus
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  fontFamily: 'inherit',
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1B5E20',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': { color: '#1B5E20' },
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineRounded sx={{ color: '#9CA3AF', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              autoComplete="current-password"
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  fontFamily: 'inherit',
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1B5E20',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': { color: '#1B5E20' },
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{ color: '#9CA3AF', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        sx={{ color: '#9CA3AF' }}
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{
                textTransform: 'none',
                py: 1.4,
                borderRadius: '10px',
                fontSize: '1rem',
                fontWeight: 600,
                fontFamily: 'inherit',
                backgroundColor: '#1B5E20',
                boxShadow: '0 2px 8px rgba(27, 94, 32, 0.3)',
                '&:hover': {
                  backgroundColor: '#4CAF50',
                  boxShadow: '0 4px 16px rgba(27, 94, 32, 0.4)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {/* Dev credentials hint */}
          <Typography
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 2.5,
              fontSize: '0.7rem',
              color: '#D1D5DB',
              fontFamily: '"IBM Plex Mono", monospace',
              letterSpacing: '0.03em',
            }}
          >
            admin / Admin123!@#
          </Typography>
        </Box>

        {/* Bottom text */}
        <Typography
          sx={{
            textAlign: 'center',
            mt: 2.5,
            fontSize: '0.72rem',
            color: 'rgba(255, 255, 255, 0.45)',
            fontFamily: 'inherit',
          }}
        >
          Built for Jordan & GCC &nbsp;·&nbsp; v1.0
        </Typography>
      </Box>
    </Box>
  );
};

export default LoginPage;
